import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
// import { Switch } from "@/components/ui/switch";
// import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
// import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft } from "lucide-react";
// import { OfflineStatusPanel } from "@/components/ds/OfflineStatusPanel";
// import { clearOfflineData } from "@/components/ds/lib/DSDataLayer";
// import { PlayerKioskInstructions } from "../player/playerDetail/PlayerKioskInstructions";
import { Link } from "react-router";
import { ROUTES } from "@/constants/routeConstants";
import { Switch } from "@/components/ui/switch";
import { exitFullScreen, enterFullScreen } from "./DSScreenUtils";

export const DSSettingsPage = () => {
  const { t } = useTranslation();
  const [isFullScreenMode, setIsFullScreenMode] = useState(localStorage.getItem("isFullScreenMode") === "true");
  const [screenDimensions, setScreenDimensions] = useState({ width: 0, height: 0 });
  const [volume, setVolume] = useState(() => {
    // Initialize volume from localStorage or default to 100
    if (typeof window !== "undefined") {
      const savedVolume = localStorage.getItem("dsVolume");
      return savedVolume ? parseInt(savedVolume, 10) : 100;
    }
    return 100;
  });
  // const [showKioskInstructions, setShowKioskInstructions] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const registeredCode = localStorage.getItem("Code");

  // Initialize screen dimensions and handle resize
  useEffect(() => {
    const updateScreenDimensions = () => {
      setScreenDimensions({
        width: window.screen.width,
        height: window.screen.height,
      });
    };

    // Set initial dimensions
    updateScreenDimensions();

    // Listen for resize events
    window.addEventListener("resize", updateScreenDimensions);

    return () => {
      window.removeEventListener("resize", updateScreenDimensions);
    };
  }, []);

  // Initialize Web Audio API
  useEffect(() => {
    try {
      audioContextRef.current = new AudioContext();
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(audioContextRef.current.destination);

      // Create a silent audio source to keep the context active
      const oscillator = audioContextRef.current.createOscillator();
      oscillator.connect(gainNodeRef.current);
      oscillator.start();
      oscillator.frequency.setValueAtTime(0, audioContextRef.current.currentTime);

      return () => {
        oscillator.stop();
        audioContextRef.current?.close();
      };
    } catch (error) {
      console.error("Failed to initialize Web Audio API:", error);
    }
  }, []);

  // Handle volume changes
  useEffect(() => {
    if (gainNodeRef.current) {
      // Convert percentage to decibels (0% = -Infinity dB, 100% = 0 dB)
      const gainValue = volume === 0 ? -Infinity : 20 * Math.log10(volume / 100);
      gainNodeRef.current.gain.setValueAtTime(gainValue, audioContextRef.current?.currentTime || 0);
    }
    // Save volume to localStorage
    localStorage.setItem("dsVolume", volume.toString());
  }, [volume]);

  const handleKioskModeToggle = () => {
    if (isFullScreenMode) {
      exitFullScreen();
      localStorage.setItem("isFullScreenMode", "false");
      setIsFullScreenMode(false);
    } else {
      enterFullScreen();
      localStorage.setItem("isFullScreenMode", "true");
      setIsFullScreenMode(true);
    }
  };

  // Add reset settings function
  const handleResetSettings = async () => {
    localStorage.removeItem("LicenceExpired");
    localStorage.removeItem("UniqueId");
    localStorage.removeItem("Code");
    location.href = ROUTES.DS;
    // await clearOfflineData();
  };

  return (
    <>
      <div className="container mx-auto p-4 max-w-2xl">
        <Link to={ROUTES.DS}>
          <Button variant="ghost" className="mb-4" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("settings.backToPlayer")}
          </Button>
        </Link>
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{t("settings.title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Registration Code Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">{t("settings.registrationCode")}</h3>
                <div className="text-sm font-mono bg-muted p-2 rounded-md mt-2 border-dashed border">
                  {registeredCode || "-"}
                </div>
              </div>
            </div>

            {/* Screen Dimensions Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">Screen Dimensions</h3>
                <div className="text-sm font-mono bg-muted p-2 rounded-md mt-2 border-dashed border">
                  {screenDimensions.width} x {screenDimensions.height} px
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">{t("settings.fullScreen")}</h3>
                <p className="text-sm text-muted-foreground">{t("settings.kioskModeDescription")}</p>
              </div>
              <Switch checked={isFullScreenMode} onCheckedChange={handleKioskModeToggle} />
            </div>

            {/* Kiosk Mode Section */}
            {/* <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">{t("settings.fullScreen")}</h3>
                  <p className="text-sm text-muted-foreground">{t("settings.kioskModeDescription")}</p>
                </div>
                <Switch checked={isFul} onCheckedChange={handleKioskModeToggle} />
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowKioskInstructions(!showKioskInstructions)}
              >
                {t("settings.showKioskInstructions")}
              </Button>

              {showKioskInstructions && (
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>{t("settings.kioskInstructionsTitle")}</AlertTitle>
                  <AlertDescription className="mt-2 space-y-2">
                    <PlayerKioskInstructions />
                  </AlertDescription>
                </Alert>
              )}
            </div> */}

            {/* Volume Control Section */}
            {/* <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">{t("settings.volume")}</h3>
                <p className="text-sm text-muted-foreground">{t("settings.volumeDescription")}</p>
              </div>
              <div className="flex items-center space-x-4">
                <Slider
                  value={[volume]}
                  onValueChange={(value) => setVolume(value[0])}
                  max={100}
                  step={1}
                  className="flex-1"
                />
                <span className="w-12 text-right">{volume}%</span>
              </div>
            </div> */}

            {/* Reset Settings Section */}
            <div className="space-y-4 pt-4 border-t">
              <div>
                <h3 className="text-lg font-medium">{t("settings.reset")}</h3>
                <p className="text-sm text-muted-foreground">{t("settings.resetDescription")}</p>
              </div>
              {/* <OfflineStatusPanel /> */}
              <Button variant="destructive" onClick={handleResetSettings} className="w-full">
                {t("settings.resetButton")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="fixed bottom-4 right-4">
        <img src="/logo.svg" alt="Logo" className="h-[32px] object-contain mt-4" />
      </div>
    </>
  );
};
